/**
 * Campuzon Phase 1 — identity, organization, membership, role, workspace.
 *
 * The point of this module is that these are FIVE separate things, and none of
 * them is a field on the user:
 *
 *   IDENTITY      who the person is                    (types/auth.ts)
 *   ORGANIZATION  the entity they participate in       (here)
 *   MEMBERSHIP    their authorised participation in it (here)
 *   ORG ROLE      the capacity that membership carries (here)
 *   WORKSPACE     the product environment it opens     (here)
 *   PERMISSION    the actions it allows, and where     (here)
 *
 * Authentication establishes the first. Everything after it is authorization,
 * and it is resolved from the authoritative store keyed on the authenticated
 * identity id — never from anything the caller supplied.
 *
 * Scope is reused from types/governance.ts rather than re-invented, so the
 * public and internal domains describe "where" the same way.
 */
import type { Scope } from './governance'

/* ------------------------------------------------------------ organizations */

/**
 * The kinds of entity a person can participate in. Housing is deliberately
 * absent: a housing operator is a PROVIDER organization carrying the housing
 * capability, so it reuses the provider identity architecture rather than
 * becoming a parallel one.
 */
export type OrganizationKind =
  | 'institution'
  | 'provider'
  | 'employer'
  | 'community'

export type OrganizationStatus =
  | 'onboarding'
  | 'active'
  | 'suspended'
  | 'closed'

/**
 * Organization verification is a DIFFERENT question from identity
 * verification. "This person controls this email" and "this business is who it
 * says it is" are never the same claim, and are never stored in one flag.
 */
export interface OrganizationVerification {
  state: 'unverified' | 'in_review' | 'verified' | 'rejected'
  /** Who established it — never Campuzon asserting on its own authority. */
  source: string | null
  checkedAt: string | null
  /** Stated plainly so a badge can never imply more than was established. */
  doesNotAssert: string
}

export interface Organization {
  id: string
  kind: OrganizationKind
  name: string
  /** The registered entity, where it differs from the trading name. */
  legalName: string | null
  /** The governance tenant this organization's data belongs to. */
  tenantId: string
  /** Set for institutions and for organizations operating at one institution. */
  institutionId: string | null
  campusIds: string[]
  status: OrganizationStatus
  verification: OrganizationVerification
  /** Capabilities the organization is authorised to operate, e.g. 'housing'. */
  capabilities: string[]
  createdAt: string
  /** Development record. Never presented as a real-world relationship. */
  isDemo: boolean
}

/* -------------------------------------------------------------- memberships */

/**
 * A membership has a life. Revoking one must not erase that it existed, or the
 * audit trail loses the only record of what someone could once reach.
 */
export type MembershipStatus =
  | 'invited'
  | 'pending'
  | 'active'
  | 'suspended'
  | 'revoked'
  | 'declined'

/** The statuses that grant anything at all. Everything else grants nothing. */
export const GRANTING_STATUSES: readonly MembershipStatus[] = ['active']

export interface Membership {
  id: string
  identityId: string
  /**
   * Null for the personal membership — the student workspace is owned by the
   * identity itself, not by an organization. Modelling it as a membership too
   * means there is exactly ONE authorization path, not two.
   */
  organizationId: string | null
  orgRoleId: string
  status: MembershipStatus
  /** Where inside the organization this membership may act. */
  scope: Scope
  invitedBy: string | null
  invitedAt: string | null
  joinedAt: string | null
  endedAt: string | null
  /** Why a membership was suspended or revoked. Shown, never silent. */
  statusReason: string | null
}

/* ------------------------------------------------------------------- roles */

/**
 * A public role is a named bundle of permissions plus the workspaces it opens.
 * Internal administration is NOT in this register — see types/governance.ts.
 */
export interface OrgRole {
  id: string
  name: string
  /** 'personal' roles attach to the identity; the rest to an organization. */
  appliesTo: OrganizationKind | 'personal'
  description: string
  permissions: PublicPermission[]
  workspaces: WorkspaceKind[]
  /** True when the role may invite and manage other members. */
  canManageMembers?: boolean
}

/** A single public action, always `domain.resource.action`. */
export type PublicPermission = string

export interface PublicPermissionRecord {
  key: PublicPermission
  label: string
  domain: string
  /** What the permission does NOT allow, so nobody has to infer it. */
  note: string
}

/* -------------------------------------------------------------- workspaces */

export type WorkspaceKind =
  | 'student'
  | 'campus'
  | 'provider'
  | 'opportunity'
  | 'community'
  | 'housing'

export interface WorkspaceDescriptor {
  kind: WorkspaceKind
  label: string
  /** What the person actually does here, for the switcher. */
  purpose: string
  path: string
  /** Who the workspace belongs to. Ambiguous ownership is not allowed. */
  ownerKind: 'identity' | 'organization'
}

/**
 * A workspace the signed-in identity is genuinely authorised to enter, with
 * the membership that authorised it attached. This is only ever produced by
 * the resolver — it is never constructed from a route parameter or a stored
 * preference, because that would make selecting a workspace into a way of
 * being granted one.
 */
export interface ResolvedWorkspace {
  /** Stable per membership, so two provider orgs are two workspaces. */
  id: string
  kind: WorkspaceKind
  label: string
  /** The organization or person the workspace belongs to. */
  ownerLabel: string
  path: string
  organizationId: string | null
  membershipId: string
  roleId: string
  roleName: string
  scope: Scope
  permissions: PublicPermission[]
}

/* ----------------------------------------------------------- authorization */

export type AuthorizationDenial =
  | 'not_authenticated'
  | 'account_not_active'
  | 'no_membership'
  | 'membership_not_active'
  | 'no_permission'
  | 'out_of_scope'
  | 'not_resource_owner'
  | 'internal_only'

export interface AuthorizationDecision {
  allowed: boolean
  reason: AuthorizationDenial | null
  /** Which membership carried it, for the audit record. */
  viaMembership: string | null
  /** Plain language, safe to show. Never leaks another tenant's existence. */
  message: string
}

/**
 * What a request is being made against. Any field present is CHECKED — an
 * organizationId the caller supplies is treated as a claim to be validated,
 * never as proof of anything.
 */
export interface ResourceRef {
  organizationId?: string | null
  identityId?: string | null
  institutionId?: string | null
  campusId?: string | null
}

/* ------------------------------------------------------------- invitations */

export type InvitationState =
  | 'sent'
  | 'accepted'
  | 'declined'
  | 'expired'
  | 'revoked'

export interface Invitation {
  id: string
  organizationId: string
  /** The invitation is addressed to an email, not to an existing account. */
  email: string
  orgRoleId: string
  scope: Scope
  invitedBy: string
  createdAt: string
  expiresAt: string
  state: InvitationState
  /** Set once accepted, so the resulting membership is traceable. */
  membershipId: string | null
}

/* ------------------------------------------------- internal administration */

/**
 * The bridge between a Campuzon identity and an internal staff record.
 *
 * It exists as a separate, explicitly provisioned table for one reason: there
 * must be no path from public signup, public role selection or any client
 * request to internal authorization. An identity is internal only if somebody
 * put a row here.
 */
export interface InternalStaffLink {
  identityId: string
  staffUserId: string
  provisionedBy: string
  provisionedAt: string
}

/** A refused attempt, recorded exactly like a successful one. */
export interface AuthorizationAttempt {
  id: string
  at: string
  identityId: string | null
  permission: string
  resource: string
  decision: AuthorizationDecision
}
